import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';

// Define theme styles for different variants
const themeStyles = {
  default: {
    icon: InfoOutlinedIcon,
    colors: {
      icon: '#3F51B5',
      headerBg: '#F8F9FA',
      titleColor: '#202124',
      buttonColor: 'primary'
    }
  },
  error: {
    icon: ErrorOutlineIcon,
    colors: {
      icon: '#D92D20',
      headerBg: '#FEF3F2',
      titleColor: '#D92D20',
      buttonColor: 'error'
    }
  }
};

const AlertModal = (props) => {
  const { 
    open, 
    handleClose, 
    message, 
    title, 
    onConfirm, 
    variant = 'default', // default or error
    isHtml = false // New prop to indicate if message contains HTML
  } = props;

  // Get the appropriate theme based on variant
  const theme = themeStyles[variant] || themeStyles.default;
  const IconComponent = theme.icon;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle 
        id="alert-dialog-title" 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: 2,
          bgcolor: theme.colors.headerBg
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <IconComponent sx={{ color: theme.colors.icon, mr: 1.5 }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: theme.colors.titleColor }}>
            {title}
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: 3, bgcolor: variant === 'error' ? theme.colors.headerBg : 'inherit' }}>
        {isHtml ? (
          <Typography 
            variant="body1" 
            component="div" 
            sx={{ color: '#424242' }}
            dangerouslySetInnerHTML={{ __html: message }}
          />
        ) : (
          <Typography 
            variant="body1" 
            component="div" 
            sx={{ 
              color: '#424242',
              whiteSpace: 'pre-line' // This preserves newlines in the text
            }}
          >
            {message}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        padding: 2, 
        justifyContent: 'flex-end',
        bgcolor: variant === 'error' ? theme.colors.headerBg : 'inherit'
      }}>
        {onConfirm ? (
          <>
            <Button onClick={handleClose} variant="outlined" color="inherit" sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button onClick={onConfirm} variant="contained" color={theme.colors.buttonColor} autoFocus>
              Confirm
            </Button>
          </>
        ) : (
          <Button 
            onClick={handleClose} 
            variant="contained" 
            color={theme.colors.buttonColor}
            autoFocus
            sx={{ 
              px: 3, 
              py: 1,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AlertModal;